<script lang="ts">
import { defineComponent, defineAsyncComponent, onActivated, onBeforeUnmount, onMounted, ref, watch, computed, nextTick } from 'vue'
import { useWorkers, type Worker } from '@/composables/useWorkers'
import { useLeaders } from '@/composables/useLeaders'
import { useJobs, useWorkerControlJobs, type Job, type WorkerControlJobName } from '@/composables/useJobs'
import { useAssignments } from '@/composables/useAssignments'
import { useEventLog } from '@/composables/useEventLog'
import { useAdmin } from '@/composables/useAdmin'
import { useDrawer } from '@/composables/useDrawer'
import { onBeforeRouteUpdate, useRoute } from 'vue-router'
import { useProjects } from '@/composables/useProjects'
import { useAuthStore } from '@/stores/auth'
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from 'primevue/usetoast'

import { useRouter } from 'vue-router'
import ActiveProcessing from '@/components/ActiveProcessing.vue'
import WorkflowPipelineView from '@/views/WorkflowPipelineView.vue'

const CommandControlPanelView = defineAsyncComponent(() => import('@/views/CommandControlPanelView.vue'))

interface EventLog {
  id: number;
  timestamp: string;
  model_name: string;
  action: string;
  instance_id: number;
  details: string;
  user: number;
  user_username?: string;
  ip_address?: string;
  request_data?: any;
  [key: string]: any; // Add index signature to allow string indexing
}

type WorkerSortKey = 'id' | 'machine_id' | 'last_seen' | 'completed_jobs' | 'avg_timing' | 'capabilities'
type WorkerSortDirection = 'asc' | 'desc'
type JobSortKey = 'id' | 'name' | 'project' | 'worker' | 'created' | 'updated' | 'args'
type JobSortDirection = 'asc' | 'desc'
type AssignmentSortKey = 'id' | 'job' | 'worker' | 'completed'
type AssignmentSortDirection = 'asc' | 'desc'
type WorkerActionValue = 'delete' | WorkerControlJobName
type ProcessingTab = 'projects' | 'jobs' | 'workers' | 'leaders' | 'assignments' | 'events' | 'admin' | 'pipeline' | 'command-control'

const LEGACY_TAB_KEYS: Record<string, ProcessingTab> = {
  '0': 'projects',
  '1': 'jobs',
  '2': 'workers',
  '3': 'assignments',
  '4': 'events',
  '5': 'admin',
  '6': 'pipeline',
  '7': 'command-control',
  '8': 'leaders'
}

export default defineComponent({
  props: {
    active: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  components: {
    ActiveProcessing,
    WorkflowPipelineView,
    CommandControlPanelView
  },
  setup(props) {
    // Call the composables to get items
    const { workers, fetchItems: fetchWorkers, workerKeys } = useWorkers()
    const { leaders, fetchItems: fetchLeaders } = useLeaders()
    const { jobs, fetchJobs, jobKeys } = useJobs()
    const { assignments, fetchAssignments, assignmentKeys } = useAssignments()
    const { eventLogs, fetchEventLog, eventLogKeys, totalCount, currentPage, pageSize, loading, hasNext, hasPrevious, totalPages, nextPage, previousPage, goToPage, changePageSize } = useEventLog()
    const { cleanupSystem, clearEventLog, createTestWorker, updateTestWorker, deleteTestWorker, error, success } = useAdmin()


    const router = useRouter()
    const route = useRoute()
    const authStore = useAuthStore()
    const confirmDialog = useConfirm()
    const toast = useToast()
    const projectsInterface = useProjects()
    const activeTab = ref<ProcessingTab>('projects')
    const highlightedRowId = ref('')
    const showScrollToTop = ref(false)
    const completionImporting = ref(false)
    const completionImportSummary = ref('')
    const completionReport = ref<Array<Record<string, any>>>([])
    let highlightTimeout: ReturnType<typeof setTimeout> | undefined

    const updateScrollToTopVisibility = () => {
      showScrollToTop.value = window.scrollY > 400
    }

    const scrollToTop = () => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    onMounted(() => {
      updateScrollToTopVisibility()
      window.addEventListener('scroll', updateScrollToTopVisibility, { passive: true })
    })

    onBeforeUnmount(() => {
      window.removeEventListener('scroll', updateScrollToTopVisibility)
      if (highlightTimeout) {
        clearTimeout(highlightTimeout)
      }
    })
    const {
      workerControlOptions,
      hasWorkerAssignment,
      getAssignmentByWorkerId,
      getWorkerControlProjectId,
      getWorkerControlSelection,
      setWorkerControlSelection,
      isWorkerControlSubmitting,
      submitWorkerControlJob
    } = useWorkerControlJobs({
      assignments,
      jobs,
      projects: projectsInterface.projects,
      fetchJobs
    })

    const workerActionOptions: Array<{ label: string; value: WorkerActionValue }> = [
      { label: 'Delete', value: 'delete' },
      ...workerControlOptions
    ]
    const workerActionSelection = ref<Record<number, WorkerActionValue>>({})

    const parseTabFromQuery = (rawTab: unknown): ProcessingTab => {
      const tabValue = Array.isArray(rawTab) ? rawTab[0] : rawTab
      if (tabValue == null) {
        return 'projects'
      }

      const normalized = String(tabValue).toLowerCase()
      if (normalized === 'queued') {
        return 'jobs'
      }
      if (normalized === 'control') {
        return 'command-control'
      }
      if (['projects', 'jobs', 'workers', 'leaders', 'assignments', 'events', 'admin', 'pipeline', 'command-control'].includes(normalized)) {
        return normalized as ProcessingTab
      }
      return LEGACY_TAB_KEYS[normalized] ?? 'projects'
    }

    const applyTabFromRoute = (rawTab: unknown) => {
      activeTab.value = parseTabFromQuery(rawTab)
    }
    
    // Track which details are expanded
    const expandedDetails = ref<Record<number, boolean>>({})

    const helpDrawer = useDrawer()
    
    const hasTestWorker = computed(() => {
      return workers.value.some(w => w.machine_id === 'test-worker-dummy')
    })

    const workerCount = computed(() => workers.value.length)
    const jobCount = computed(() => jobs.value.length)
    const assignmentCount = computed(() => assignments.value.length)

    const assignedWorkerCount = computed(() => {
      const workerIds = new Set(
        workers.value
          .map((worker) => Number(worker.id))
          .filter((id) => Number.isFinite(id))
      )

      const assignedIds = new Set<number>()
      assignments.value.forEach((assignment: any) => {
        const workerId = Number(assignment?.worker)
        if (Number.isFinite(workerId) && workerIds.has(workerId)) {
          assignedIds.add(workerId)
        }
      })

      return assignedIds.size
    })

    const workerUtilization = computed(() => {
      if (workerCount.value === 0) {
        return 0
      }
      return Math.min(1, assignedWorkerCount.value / workerCount.value)
    })

    const workerUtilizationPercent = computed(() => Math.round(workerUtilization.value * 100))

    const workerUtilizationColor = computed(() => {
      const hue = Math.round(workerUtilization.value * 120)
      return `hsl(${hue}, 78%, 42%)`
    })
    
    helpDrawer.header = 'Processing Help'
    helpDrawer.content = [
      "This page shows the current state of the processing system.",
      "The 'Projects' tab shows the projects that are currently in progress.",
      "The 'Jobs queued' table shows the jobs that are waiting to be processed.",
      "The 'Workers' table shows the workers that are connected to the system.",
      "The 'Assigned jobs' table shows the jobs that are currently being processed.",
      "The 'Event log' table shows the events that have occurred in the system.",
      "The 'Admin' tab provides system cleanup options for administrators.",
    ]

    // Fetch items when the component is mounted
    onMounted( async () => {
      applyTabFromRoute(route.query.tab)
      await fetchWorkers()
      await fetchLeaders()
      await hydrateWorkerUserNames()
      fetchJobs()
      fetchAssignments()
      fetchEventLog()
      await projectsInterface.fetchProjects().then(() => {
        console.log("Projects:", projectsInterface.projects.value);
      });
      console.log('ProcessingView mounted')
    })

    watch(workers, () => {
      void hydrateWorkerUserNames()
    }, { deep: false })

    onActivated(() => {
      console.log('ProcessingView help drawer')
      helpDrawer.header = 'Processing Help'
      helpDrawer.content = [
        "This page shows the current state of the processing system.",
        "The 'Jobs queued' table shows the jobs that are waiting to be processed.",
        "The 'Workers' table shows the workers that are connected to the system.",
        "The 'Assigned jobs' table shows the jobs that are currently being processed.",
        "The 'Event log' table shows the events that have occurred in the system.",
        "The 'Admin' tab provides system cleanup options for administrators.",
      ]
    })

    watch(() => props.active, (newVal) => {
      console.log('ProcessingView active', newVal)
      if (newVal) {
        helpDrawer.header = 'Processing Help'
        helpDrawer.content = [
          "This page shows the current state of the processing system.",
          "The 'Jobs queued' table shows the jobs that are waiting to be processed.",
          "The 'Workers' table shows the workers that are connected to the system.",
          "The 'Assigned jobs' table shows the jobs that are currently being processed.",
          "The 'Event log' table shows the events that have occurred in the system.",
          "The 'Admin' tab provides system cleanup options for administrators.",
        ]
      }
    })

    onBeforeRouteUpdate((to) => {
      applyTabFromRoute(to.query.tab)
    })

    // Manually define the columns and their widths for each table
    const tableConfig = {
      projects: {
        headers: ['id', 'author_username', 'name', 'area_of_interest_name', 'start_date', 'end_date', 'status'],
        columnWidths: ['8%', '15%', '20%', '15%', '14%', '14%', '14%'],
      },
      jobs: {
        headers: ['id', 'name', 'project', 'worker', 'created', 'updated', 'args'],
        columnWidths: ['9%', '13%', '14%', '13%', '14%', '14%', '15%', '8%'],
      },
      workers: {
        columnWidths: ['7%', '22%', '18%', '18%', '9%', '8%', '10%', '8%'],
      },
      assignments: {
        headers: ['id', 'job', 'worker', 'completed'],
        columnWidths: ['22%', '22%', '22%', '22%', '12%'],
      },
      eventLog: {
        headers: ['timestamp', 'model_name', 'action', 'user_username', 'ip_address', 'details'],
        columnWidths: ['15%', '10%', '8%', '12%', '10%', '45%'],
      }
    }

    const formatHeaderKey = (key: string) => {
      return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase())
    }

    const getWorkerById = (workerId: unknown) => {
      const id = Number(workerId)
      if (!Number.isFinite(id)) {
        return null
      }
      return workers.value.find((w) => Number(w.id) === id) ?? null
    }

    const getProjectById = (projectId: unknown) => {
      const id = Number(projectId)
      if (!Number.isFinite(id)) {
        return null
      }
      return projectsInterface.projects.value.find((p) => Number(p.id) === id) ?? null
    }

    const getWorkerDisplay = (workerId: unknown) => {
      const worker = getWorkerById(workerId)
      if (!worker) {
        return String(workerId ?? '')
      }
      return worker.machine_id || String(worker.id ?? workerId ?? '')
    }

    const getProjectDisplay = (projectId: unknown) => {
      const project = getProjectById(projectId)
      if (!project) {
        return String(projectId ?? '')
      }
      return project.name || String(project.id ?? projectId ?? '')
    }

    const getHeartbeatAgeMs = (updated: unknown) => {
      if (typeof updated !== 'string' || !updated) {
        return Number.POSITIVE_INFINITY
      }
      const timestamp = new Date(updated).getTime()
      if (Number.isNaN(timestamp)) {
        return Number.POSITIVE_INFINITY
      }
      return Date.now() - timestamp
    }

    const getWorkerLastSeenTimestamp = (worker: Worker | null | undefined) => {
      if (!worker) {
        return ''
      }
      return typeof worker.last_seen === 'string' && worker.last_seen
        ? worker.last_seen
        : typeof worker.updated === 'string' && worker.updated
          ? worker.updated
          : ''
    }

    const getWorkerLastSeenDate = (worker: Worker | null | undefined) => {
      const lastSeen = getWorkerLastSeenTimestamp(worker)
      if (!lastSeen) {
        return null
      }
      const date = new Date(lastSeen)
      return Number.isNaN(date.getTime()) ? null : date
    }

    const getWorkerHeartbeatClass = (workerId: unknown) => {
      const worker = getWorkerById(workerId)
      const ageMs = getHeartbeatAgeMs(getWorkerLastSeenTimestamp(worker))

      if (ageMs <= 60_000) {
        return 'heartbeat-fresh'
      }
      if (ageMs > 300_000) {
        return 'heartbeat-stale'
      }
      if (ageMs > 150_000) {
        return 'heartbeat-warning'
      }
      return ''
    }

    const getWorkerHeartbeatClassFromWorker = (worker: Worker) => {
      const ageMs = getHeartbeatAgeMs(getWorkerLastSeenTimestamp(worker))

      if (ageMs <= 60_000) {
        return 'heartbeat-fresh'
      }
      if (ageMs > 300_000) {
        return 'heartbeat-stale'
      }
      if (ageMs > 150_000) {
        return 'heartbeat-warning'
      }
      return ''
    }

    const getLeaderHeartbeatClass = (leader: { last_seen?: string; updated?: string }) => {
      const timestamp = leader.last_seen || leader.updated || ''
      const ageMs = getHeartbeatAgeMs(timestamp)

      if (ageMs <= 60_000) {
        return 'heartbeat-fresh'
      }
      if (ageMs > 300_000) {
        return 'heartbeat-stale'
      }
      if (ageMs > 150_000) {
        return 'heartbeat-warning'
      }
      return ''
    }

    const formatDateTime = (value: unknown) => {
      if (typeof value !== 'string' || !value) {
        return 'unknown'
      }
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return 'unknown'
      }
      return date.toLocaleString()
    }

    const getWorkerCompletedJobs = (worker: Worker) => {
      const completedJobs = worker.notes?.completed_jobs
      return Array.isArray(completedJobs) ? completedJobs : []
    }

    const getWorkerCompletedJobCount = (worker: Worker) => getWorkerCompletedJobs(worker).length

    const getWorkerAverageTimingSeconds = (worker: Worker) => {
      const timings = getWorkerCompletedJobs(worker)
        .map((job) => Number(job?.timing))
        .filter((timing) => Number.isFinite(timing))

      if (timings.length === 0) {
        return null
      }

      const total = timings.reduce((sum, value) => sum + value, 0)
      return total / timings.length
    }

    const formatTiming = (seconds: number | null) => {
      if (seconds == null || !Number.isFinite(seconds)) {
        return 'n/a'
      }
      return `${seconds.toFixed(1)}s`
    }

    const getWorkerCapabilities = (worker: Worker) => {
      const capabilities = worker.notes?.capabilities
      if (!Array.isArray(capabilities)) {
        return [] as string[]
      }
      return capabilities
        .map((capability) => String(capability).trim())
        .filter((capability) => capability.length > 0)
    }

    const formatCapabilities = (worker: Worker) => {
      const capabilities = getWorkerCapabilities(worker)
      return capabilities.length > 0 ? capabilities.join(', ') : 'none'
    }

    const userNameById = ref<Record<number, string>>({})
    const usersInFlight = new Set<number>()

    const fetchUserName = async (userId: unknown) => {
      const id = Number(userId)
      if (!Number.isFinite(id) || id <= 0) {
        return
      }
      if (userNameById.value[id] || usersInFlight.has(id)) {
        return
      }

      usersInFlight.add(id)
      try {
        const response = await fetch(`/api/users/${id}/`)
        if (!response.ok) {
          return
        }
        const payload = await response.json()
        const username = typeof payload?.username === 'string' ? payload.username : ''
        if (username) {
          userNameById.value = {
            ...userNameById.value,
            [id]: username
          }
        }
      } catch (e) {
        console.error(e)
      } finally {
        usersInFlight.delete(id)
      }
    }

    const hydrateWorkerUserNames = async () => {
      const uniqueIds = [...new Set(
        workers.value
          .map((worker) => Number(worker.user))
          .filter((id) => Number.isFinite(id) && id > 0)
      )]
      await Promise.all(uniqueIds.map((id) => fetchUserName(id)))
    }

    const formatCapabilityLabel = (capability: string) => {
      const normalized = capability.trim().toLowerCase()
      if (normalized === 'download') {
        return 'Download'
      }

      if (normalized.startsWith('memory_gb:')) {
        const value = capability.split(':')[1]
        const numeric = Number(value)
        if (Number.isFinite(numeric)) {
          return `${numeric} GB RAM`
        }
      }

      return capability
        .replace(/_/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase())
    }

    const getWorkerUserDisplay = (worker: Worker) => {
      const id = Number(worker.user)
      if (!Number.isFinite(id)) {
        return 'unknown'
      }
      return userNameById.value[id] ?? `#${id}`
    }

    const getCompletedJobsTooltip = (worker: Worker) => {
      const completedJobs = getWorkerCompletedJobs(worker)
      if (completedJobs.length === 0) {
        return 'No completed jobs recorded'
      }

      return completedJobs
        .slice(-12)
        .reverse()
        .map((job, index) => {
          const name = job?.name ?? 'Unknown'
          const status = job?.status ?? 'unknown'
          const timing = Number(job?.timing)
          const timingLabel = Number.isFinite(timing) ? `${timing.toFixed(1)}s` : 'n/a'
          const completedAt = typeof job?.completed_at === 'string' && job.completed_at
            ? formatDateTime(job.completed_at)
            : 'unknown time'
          return `${index + 1}. ${name} [${status}] - ${timingLabel} - ${completedAt}`
        })
        .join('\n')
    }

    const recentCompletedJobs = computed(() => {
      const entries: Array<{
        workerMachineId: string
        workerUser: string
        name: string
        status: string
        timing: number
        completedAt: string
        completedAtMs: number
      }> = []

      workers.value.forEach((worker) => {
        const completedJobs = getWorkerCompletedJobs(worker)
        completedJobs.forEach((job) => {
          const completedAt = typeof job?.completed_at === 'string' ? job.completed_at : ''
          const completedAtMs = completedAt ? new Date(completedAt).getTime() : 0

          entries.push({
            workerMachineId: worker.machine_id,
            workerUser: getWorkerUserDisplay(worker),
            name: job?.name ?? 'Unknown',
            status: job?.status ?? 'unknown',
            timing: Number(job?.timing),
            completedAt,
            completedAtMs: Number.isFinite(completedAtMs) ? completedAtMs : 0
          })
        })
      })

      return entries.sort((left, right) => right.completedAtMs - left.completedAtMs)
    })

    const recentCompletionsCount = computed(() => recentCompletedJobs.value.length)

    const recentCompletionsLastAtLabel = computed(() => {
      const latest = recentCompletedJobs.value[0]
      if (!latest || !latest.completedAt) {
        return 'No completions yet'
      }
      return `Latest: ${formatDateTime(latest.completedAt)}`
    })

    const recentCompletionsTooltip = computed(() => {
      const items = recentCompletedJobs.value.slice(0, 8)
      if (items.length === 0) {
        return 'No recent job completions'
      }

      return items
        .map((item, index) => {
          const timingLabel = Number.isFinite(item.timing) ? `${item.timing.toFixed(1)}s` : 'n/a'
          const completedAtLabel = item.completedAt ? formatDateTime(item.completedAt) : 'unknown time'
          return `${index + 1}. ${item.name} [${item.status}] - ${timingLabel}\n   ${item.workerMachineId} (User: ${item.workerUser})\n   ${completedAtLabel}`
        })
        .join('\n')
    })

    const goToWorkerAssignment = async (worker: Worker) => {
      const assignment = getAssignmentByWorkerId(worker.id)
      if (!assignment?.id) {
        return
      }

      await scrollToRowInTab('assignments', getAssignmentRowId(assignment.id))
    }

    const workersColumnCount = 8
    const workerSortKey = ref<WorkerSortKey>('last_seen')
    const workerSortDirection = ref<WorkerSortDirection>('desc')
    const selectedViewUserFilter = ref('all')
    const jobSortKey = ref<JobSortKey>('updated')
    const jobSortDirection = ref<JobSortDirection>('desc')
    const assignmentSortKey = ref<AssignmentSortKey>('id')
    const assignmentSortDirection = ref<AssignmentSortDirection>('desc')

    const getWorkerRowId = (workerId: unknown) => `worker-${String(workerId ?? '')}`
    const getJobRowId = (jobId: unknown) => `job-${String(jobId ?? '')}`
    const getAssignmentRowId = (assignmentId: unknown) => `assignment-${String(assignmentId ?? '')}`

    const scrollToRowInTab = async (tabValue: ProcessingTab, rowId: string) => {
      activeTab.value = tabValue
      await nextTick()
      const preservedX = window.scrollX
      const target = document.getElementById(rowId)
      if (!target) {
        return
      }

      target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
      highlightedRowId.value = rowId
      if (highlightTimeout) {
        clearTimeout(highlightTimeout)
      }
      highlightTimeout = setTimeout(() => {
        highlightedRowId.value = ''
      }, 2600)
      requestAnimationFrame(() => {
        if (window.scrollX !== preservedX) {
          window.scrollTo({ left: preservedX, top: window.scrollY, behavior: 'auto' })
        }
      })
    }

    const getJobById = (jobId: unknown) => {
      const id = Number(jobId)
      if (!Number.isFinite(id)) {
        return null
      }
      return jobs.value.find((job: any) => Number(job?.id) === id) ?? null
    }

    const getJobDisplay = (jobId: unknown) => {
      const job = getJobById(jobId)
      if (!job) {
        return String(jobId ?? '')
      }
      return String(job.name ?? job.id ?? jobId ?? '')
    }

    const canLinkToWorker = (workerId: unknown) => {
      return Boolean(getWorkerById(workerId)?.id)
    }

    const canLinkToJob = (jobId: unknown) => {
      return Boolean(getJobById(jobId)?.id)
    }

    const goToWorkerFromJob = async (workerId: unknown) => {
      const worker = getWorkerById(workerId)
      if (!worker?.id) {
        return
      }
      await scrollToRowInTab('workers', getWorkerRowId(worker.id))
    }

    const goToJobFromAssignment = async (jobId: unknown) => {
      const job = getJobById(jobId)
      if (!job?.id) {
        return
      }
      await scrollToRowInTab('jobs', getJobRowId(job.id))
    }

    const getJobOwnerUsername = (job: any) => {
      const project = getProjectById(job?.project)
      const projectAuthor = typeof project?.author_username === 'string' ? project.author_username.trim() : ''
      if (projectAuthor) {
        return projectAuthor
      }

      const worker = getWorkerById(job?.worker)
      if (worker) {
        return getWorkerUserDisplay(worker)
      }

      return 'unknown'
    }

    const getAssignmentOwnerUsername = (assignment: any) => {
      const job = getJobById(assignment?.job)
      if (job) {
        return getJobOwnerUsername(job)
      }

      const worker = getWorkerById(assignment?.worker)
      if (worker) {
        return getWorkerUserDisplay(worker)
      }

      return 'unknown'
    }

    const userFilterOptions = computed(() => {
      const users = new Set<string>()
      jobs.value.forEach((job: any) => {
        users.add(getJobOwnerUsername(job))
      })
      workers.value.forEach((worker) => {
        users.add(getWorkerUserDisplay(worker))
      })
      assignments.value.forEach((assignment: any) => {
        users.add(getAssignmentOwnerUsername(assignment))
      })
      return Array.from(users).sort((left, right) => left.localeCompare(right))
    })

    const matchesSelectedUserFilter = (username: string) => {
      if (selectedViewUserFilter.value === 'all') {
        return true
      }
      return username === selectedViewUserFilter.value
    }

    const getJobModifiedTimeMs = (job: any) => {
      const updated = typeof job?.updated === 'string' ? job.updated : ''
      const created = typeof job?.created === 'string' ? job.created : ''
      const raw = updated || created
      if (!raw) {
        return 0
      }
      const parsed = new Date(raw).getTime()
      return Number.isFinite(parsed) ? parsed : 0
    }

    const getJobCreatedTimeMs = (job: any) => {
      const created = typeof job?.created === 'string' ? job.created : ''
      if (!created) {
        return 0
      }
      const parsed = new Date(created).getTime()
      return Number.isFinite(parsed) ? parsed : 0
    }

    const jobSortValue = (job: any, key: JobSortKey) => {
      if (key === 'id') {
        return Number(job?.id ?? 0)
      }
      if (key === 'name') {
        return String(job?.name ?? '').toLowerCase()
      }
      if (key === 'project') {
        return getProjectDisplay(job?.project).toLowerCase()
      }
      if (key === 'worker') {
        return getWorkerDisplay(job?.worker).toLowerCase()
      }
      if (key === 'created') {
        return getJobCreatedTimeMs(job)
      }
      if (key === 'updated') {
        return getJobModifiedTimeMs(job)
      }
      if (key === 'args') {
        return formatArgs(String(job?.args ?? '')).toLowerCase()
      }
      return 0
    }

    const asJobSortKey = (header: string): JobSortKey | null => {
      if (header === 'id' || header === 'name' || header === 'project' || header === 'worker' || header === 'created' || header === 'updated' || header === 'args') {
        return header
      }
      return null
    }

    const setJobSort = (key: JobSortKey) => {
      if (jobSortKey.value === key) {
        jobSortDirection.value = jobSortDirection.value === 'asc' ? 'desc' : 'asc'
        return
      }

      jobSortKey.value = key
      jobSortDirection.value = key === 'id' ? 'desc' : 'asc'
      if (key === 'created' || key === 'updated') {
        jobSortDirection.value = 'desc'
      }
    }

    const setJobSortFromHeader = (header: string) => {
      const key = asJobSortKey(header)
      if (!key) {
        return
      }
      setJobSort(key)
    }

    const getJobSortIndicator = (key: JobSortKey) => {
      if (jobSortKey.value !== key) {
        return '↕'
      }
      return jobSortDirection.value === 'asc' ? '▲' : '▼'
    }

    const getJobSortIndicatorForHeader = (header: string) => {
      const key = asJobSortKey(header)
      if (!key) {
        return ''
      }
      return getJobSortIndicator(key)
    }

    const filteredSortedJobs = computed(() => {
      const filtered = jobs.value.filter((job: any) => matchesSelectedUserFilter(getJobOwnerUsername(job)))
      const direction = jobSortDirection.value === 'asc' ? 1 : -1

      return [...filtered].sort((left: any, right: any) => {
        const leftValue = jobSortValue(left, jobSortKey.value)
        const rightValue = jobSortValue(right, jobSortKey.value)

        if (typeof leftValue === 'number' && typeof rightValue === 'number') {
          return (leftValue - rightValue) * direction
        }

        return String(leftValue).localeCompare(String(rightValue)) * direction
      })
    })

    const assignmentSortValue = (assignment: any, key: AssignmentSortKey) => {
      if (key === 'id') {
        return Number(assignment?.id ?? 0)
      }
      if (key === 'job') {
        return getJobDisplay(assignment?.job).toLowerCase()
      }
      if (key === 'worker') {
        return getWorkerDisplay(assignment?.worker).toLowerCase()
      }
      return Number(Boolean(assignment?.completed))
    }

    const setAssignmentSort = (key: AssignmentSortKey) => {
      if (assignmentSortKey.value === key) {
        assignmentSortDirection.value = assignmentSortDirection.value === 'asc' ? 'desc' : 'asc'
        return
      }
      assignmentSortKey.value = key
      assignmentSortDirection.value = key === 'completed' ? 'asc' : 'desc'
    }

    const getAssignmentSortIndicator = (key: AssignmentSortKey) => {
      if (assignmentSortKey.value !== key) {
        return '↕'
      }
      return assignmentSortDirection.value === 'asc' ? '▲' : '▼'
    }

    const asAssignmentSortKey = (header: string): AssignmentSortKey | null => {
      if (header === 'id' || header === 'job' || header === 'worker' || header === 'completed') {
        return header
      }
      return null
    }

    const setAssignmentSortFromHeader = (header: string) => {
      const key = asAssignmentSortKey(header)
      if (!key) {
        return
      }
      setAssignmentSort(key)
    }

    const getAssignmentSortIndicatorForHeader = (header: string) => {
      const key = asAssignmentSortKey(header)
      if (!key) {
        return ''
      }
      return getAssignmentSortIndicator(key)
    }

    const sortedAssignments = computed(() => {
      const key = assignmentSortKey.value
      const direction = assignmentSortDirection.value === 'asc' ? 1 : -1
      const filtered = assignments.value.filter((assignment: any) => matchesSelectedUserFilter(getAssignmentOwnerUsername(assignment)))

      return [...filtered].sort((left: any, right: any) => {
        const leftValue = assignmentSortValue(left, key)
        const rightValue = assignmentSortValue(right, key)

        if (typeof leftValue === 'number' && typeof rightValue === 'number') {
          return (leftValue - rightValue) * direction
        }

        return String(leftValue).localeCompare(String(rightValue)) * direction
      })
    })

    const sortWorkers = (left: Worker, right: Worker, key: WorkerSortKey) => {
      if (key === 'id') {
        return Number(left.id ?? 0) - Number(right.id ?? 0)
      }
      if (key === 'machine_id') {
        return String(left.machine_id ?? '').localeCompare(String(right.machine_id ?? ''))
      }
      if (key === 'last_seen') {
        const leftTime = getWorkerLastSeenDate(left)?.getTime() ?? 0
        const rightTime = getWorkerLastSeenDate(right)?.getTime() ?? 0
        return leftTime - rightTime
      }
      if (key === 'completed_jobs') {
        return getWorkerCompletedJobCount(left) - getWorkerCompletedJobCount(right)
      }
      if (key === 'avg_timing') {
        const leftAvg = getWorkerAverageTimingSeconds(left)
        const rightAvg = getWorkerAverageTimingSeconds(right)
        const leftSortable = leftAvg == null ? Number.POSITIVE_INFINITY : leftAvg
        const rightSortable = rightAvg == null ? Number.POSITIVE_INFINITY : rightAvg
        return leftSortable - rightSortable
      }

      const leftCapabilities = getWorkerCapabilities(left).length
      const rightCapabilities = getWorkerCapabilities(right).length
      return leftCapabilities - rightCapabilities
    }

    const sortedWorkers = computed(() => {
      const items = workers.value.filter((worker) => matchesSelectedUserFilter(getWorkerUserDisplay(worker)))
      const key = workerSortKey.value
      const direction = workerSortDirection.value === 'asc' ? 1 : -1
      return [...items].sort((left, right) => sortWorkers(left, right, key) * direction)
    })

    const setWorkerSort = (key: WorkerSortKey) => {
      if (workerSortKey.value === key) {
        workerSortDirection.value = workerSortDirection.value === 'asc' ? 'desc' : 'asc'
        return
      }
      workerSortKey.value = key
      workerSortDirection.value = key === 'machine_id' ? 'asc' : 'desc'
    }

    const getWorkerSortIndicator = (key: WorkerSortKey) => {
      if (workerSortKey.value !== key) {
        return '↕'
      }
      return workerSortDirection.value === 'asc' ? '▲' : '▼'
    }

    const formatHeartbeatAge = (updated: unknown) => {
      const ageMs = getHeartbeatAgeMs(updated)
      if (!Number.isFinite(ageMs)) {
        return 'unknown'
      }

      const minutes = Math.floor(ageMs / 60_000)
      const seconds = Math.floor((ageMs % 60_000) / 1000)
      if (minutes <= 0) {
        return `${seconds}s ago`
      }
      return `${minutes}m ${seconds}s ago`
    }

    const getWorkerTooltip = (workerId: unknown) => {
      const worker = getWorkerById(workerId)
      if (!worker) {
        return String(workerId ?? '')
      }
      return `${worker.machine_id}\nLast heartbeat: ${formatHeartbeatAge(getWorkerLastSeenTimestamp(worker))}`
    }

    const getWorkerActionSelection = (worker: Worker): WorkerActionValue => {
      const workerId = Number(worker.id)
      if (!Number.isFinite(workerId)) {
        return 'delete'
      }
      return workerActionSelection.value[workerId] ?? 'delete'
    }

    const setWorkerActionSelection = (worker: Worker, value: string) => {
      const workerId = Number(worker.id)
      if (!Number.isFinite(workerId)) {
        return
      }

      const normalized = value === 'delete' ? value : workerControlOptions.find((item) => item.value === value)?.value
      if (!normalized) {
        return
      }

      workerActionSelection.value = {
        ...workerActionSelection.value,
        [workerId]: normalized
      }

      if (normalized !== 'delete') {
        setWorkerControlSelection(worker, normalized)
      }
    }

    const isWorkerActionSubmitting = (worker: Worker) => {
      const selection = getWorkerActionSelection(worker)
      if (selection === 'delete') {
        return false
      }
      return isWorkerControlSubmitting(worker)
    }

    const getWorkerActionLabel = (worker: Worker) => {
      const selection = getWorkerActionSelection(worker)
      if (selection === 'delete') {
        return 'Delete Worker'
      }

      return isWorkerControlSubmitting(worker) ? 'Sending...' : 'Send Action'
    }

    const canRunWorkerAction = (worker: Worker) => {
      const selection = getWorkerActionSelection(worker)
      if (selection === 'delete') {
        return true
      }
      return getWorkerControlProjectId(worker) != null && !isWorkerControlSubmitting(worker)
    }

    const runWorkerAction = async (worker: Worker) => {
      const selection = getWorkerActionSelection(worker)
      if (selection === 'delete') {
        await handleDeleteWorker(worker.id)
        return
      }

      setWorkerControlSelection(worker, selection)
      await submitWorkerControlJob(worker)
    }

    const formatArgs = (args: string) => {
      if (!args) return ''
      const newArgs = args.split(',')
      const formattedArgs: { key: string; value: string }[] = []
      for (let i = 0; i < newArgs.length; i += 2) {
        if (i + 1 < newArgs.length) {
          formattedArgs.push({ key: newArgs[i], value: newArgs[i + 1] })
        }
      }
      const annoyingKeys = ['DesiredOutput']
      const filteredArgs = formattedArgs.filter(arg => !annoyingKeys.includes(arg.key))
      return filteredArgs.map(arg => `${arg.key}: ${arg.value}`).join('; ')
    }

    const formatDetails = (details: string, logId: number) => {
      try {
        // Parse the JSON string
        const detailsObj = JSON.parse(details)
        
        // Format with indentation for better readability
        const formattedJson = JSON.stringify(detailsObj, null, 2)
        const lines = formattedJson.split('\n')
        
        // If not expanded and has more than 10 lines, truncate
        if (!expandedDetails.value[logId] && lines.length > 10) {
          return lines.slice(0, 10).join('\n') + '\n...'
        }
        
        return formattedJson
      } catch (e) {
        // Return original if not valid JSON
        return details
      }
    }

    const isDetailsTruncated = (details: string) => {
      try {
        const detailsObj = JSON.parse(details)
        const formattedJson = JSON.stringify(detailsObj, null, 2)
        return formattedJson.split('\n').length > 10
      } catch (e) {
        return false
      }
    }

    const toggleDetails = (logId: number) => {
      expandedDetails.value[logId] = !expandedDetails.value[logId]
    }

    const handleCleanupSystem = async () => {
      await cleanupSystem()
      // Refresh all data after cleanup
      fetchWorkers()
      fetchJobs()
      fetchAssignments()
      fetchEventLog(undefined, undefined, { force: true })
    }

    const handleClearEventLog = async () => {
      await clearEventLog()
      fetchEventLog(undefined, undefined, { force: true })
    }

    const handleImportCompletionHistory = async () => {
      completionImporting.value = true
      completionImportSummary.value = ''
      try {
        const csrfToken = await authStore.getCsrfToken()
        const importResponse = await fetch('/api/usage-metrics/completion-events/import/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken ?? ''
          },
          body: JSON.stringify({})
        })
        if (!importResponse.ok) {
          throw new Error(`Completion import failed (${importResponse.status})`)
        }
        const importResult = await importResponse.json()
        const reportResponse = await fetch('/api/usage-metrics/completion-events/report/')
        if (!reportResponse.ok) {
          throw new Error(`Completion report failed (${reportResponse.status})`)
        }
        const reportResult = await reportResponse.json()
        completionReport.value = Array.isArray(reportResult.results) ? reportResult.results : []
        completionImportSummary.value = `Scanned ${importResult.completion_entries_scanned}; imported ${importResult.inserted}; duplicates ${importResult.duplicates}; invalid ${importResult.invalid}.`
        toast.add({ severity: 'success', summary: 'Completion History Imported', detail: completionImportSummary.value, life: 5000 })
      } catch (e) {
        console.error(e)
        toast.add({ severity: 'error', summary: 'Completion Import Failed', detail: String(e), life: 5000 })
      } finally {
        completionImporting.value = false
      }
    }

    const ensureProjectsLoaded = async () => {
      if (projectsInterface.projects.value.length === 0) {
        await projectsInterface.fetchProjects()
      }
    }

    const refreshActiveTab = async (tab: string, options: { forceEventLog?: boolean } = {}) => {
      if (tab === 'projects') {
        await projectsInterface.fetchProjects()
        return
      }

      if (tab === 'jobs') {
        await Promise.all([
          fetchJobs(),
          fetchWorkers(),
          ensureProjectsLoaded()
        ])
        await hydrateWorkerUserNames()
        return
      }

      if (tab === 'workers') {
        await fetchWorkers()
        await hydrateWorkerUserNames()
        return
      }

      if (tab === 'assignments') {
        await Promise.all([
          fetchAssignments(),
          fetchWorkers()
        ])
        await hydrateWorkerUserNames()
        return
      }

      if (tab === 'events') {
        await fetchEventLog(undefined, undefined, { force: options.forceEventLog === true })
        return
      }

      if (tab === 'leaders') {
        await fetchLeaders()
      }
    }

    const handleRefreshAll = async (options: { forceEventLog?: boolean } = {}) => {
      console.log('Refreshing all tables...')
      await Promise.all([
        fetchWorkers(),
        fetchLeaders(),
        fetchJobs(),
        fetchAssignments(),
        fetchEventLog(undefined, undefined, { force: options.forceEventLog === true }),
        projectsInterface.fetchProjects()
      ])
      await hydrateWorkerUserNames()
      console.log('All tables refreshed')
    }

    const handleManualRefreshAll = async () => {
      await handleRefreshAll({ forceEventLog: true })
    }

    const handleTabChange = async (value: string | number) => {
      activeTab.value = String(value) as ProcessingTab
      await refreshActiveTab(activeTab.value)

      const nextQuery: Record<string, any> = { ...route.query }
      nextQuery.tab = activeTab.value
      void router.replace({ query: nextQuery })
    }

    const handleCreateTestWorker = async () => {
      await createTestWorker()
      await fetchWorkers()
      await hydrateWorkerUserNames()
    }

    const handleUpdateTestWorker = async () => {
      const testWorker = workers.value.find(w => w.machine_id === 'test-worker-dummy')
      if (testWorker?.id) {
        await updateTestWorker(testWorker.id)
        await fetchWorkers()
        await hydrateWorkerUserNames()
      }
    }

    const handleDeleteTestWorker = async () => {
      const testWorker = workers.value.find(w => w.machine_id === 'test-worker-dummy')
      if (testWorker?.id) {
        await deleteTestWorker(testWorker.id)
        await fetchWorkers()
        await hydrateWorkerUserNames()
      }
    }

    const handlePageSizeChange = (event: Event) => {
      const target = event.target as HTMLSelectElement
      if (target) {
        changePageSize(Number(target.value))
      }
    }

    const deleteById = async (resource: 'jobs' | 'workers' | 'leaders' | 'assignments', id: number) => {
      const csrfToken = await authStore.getCsrfToken()
      const response = await fetch(`/api/${resource}/${id}/`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken ?? ''
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to delete ${resource.slice(0, -1)} ${id}`)
      }
    }

    const requestConfirmation = (header: string, message: string) => {
      return new Promise<boolean>((resolve) => {
        confirmDialog.require({
          header,
          message,
          icon: 'pi pi-exclamation-triangle',
          accept: () => resolve(true),
          reject: () => resolve(false)
        })
      })
    }

    const handleDeleteJob = async (jobId?: number) => {
      if (jobId == null) {
        console.warn('Cannot delete job: missing id')
        return
      }

      const confirmed = await requestConfirmation(
        'Delete Job',
        `Delete job ${jobId}? This cannot be undone.`
      )
      if (!confirmed) {
        return
      }

      try {
        await deleteById('jobs', jobId)
        await fetchJobs()
        toast.add({
          severity: 'success',
          summary: 'Job Deleted',
          detail: `Deleted job ${jobId}`,
          life: 3000
        })
      } catch (e) {
        console.error(e)
        toast.add({
          severity: 'error',
          summary: 'Delete Failed',
          detail: `Could not delete job ${jobId}`,
          life: 3500
        })
      }
    }

    const handleDeleteWorker = async (workerId?: number) => {
      if (workerId == null) {
        console.warn('Cannot delete worker: missing id')
        return
      }

      const confirmed = await requestConfirmation(
        'Delete Worker',
        `Delete worker ${workerId}? This cannot be undone.`
      )
      if (!confirmed) {
        return
      }

      try {
        await deleteById('workers', workerId)
        await fetchWorkers()
        await hydrateWorkerUserNames()
        toast.add({
          severity: 'success',
          summary: 'Worker Deleted',
          detail: `Deleted worker ${workerId}`,
          life: 3000
        })
      } catch (e) {
        console.error(e)
        toast.add({
          severity: 'error',
          summary: 'Delete Failed',
          detail: `Could not delete worker ${workerId}`,
          life: 3500
        })
      }
    }

    const handleDeleteLeader = async (leaderId?: number) => {
      if (leaderId == null) {
        console.warn('Cannot delete leader: missing id')
        return
      }

      const confirmed = await requestConfirmation(
        'Delete Leader',
        `Delete leader ${leaderId}? This cannot be undone.`
      )
      if (!confirmed) {
        return
      }

      try {
        await deleteById('leaders', leaderId)
        await fetchLeaders()
        toast.add({
          severity: 'success',
          summary: 'Leader Deleted',
          detail: `Deleted leader ${leaderId}`,
          life: 3000
        })
      } catch (e) {
        console.error(e)
        toast.add({
          severity: 'error',
          summary: 'Delete Failed',
          detail: `Could not delete leader ${leaderId}`,
          life: 3500
        })
      }
    }

    const handleScopedBulkDelete = async (
      endpoint: string,
      countKey: 'worker_count' | 'job_count' | 'assignment_count',
      label: string,
      onRefresh: () => Promise<void>
    ) => {
      const csrfToken = await authStore.getCsrfToken()

      try {
        // Step 1: preview how many rows would be deleted for this user.
        const previewResponse = await fetch('/api/workers/delete_all/', {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken ?? ''
          }
        })

        if (!previewResponse.ok) {
          throw new Error('Failed to preview worker cleanup')
        }

        const previewPayload = await previewResponse.json()
        const scopedCount = Number(previewPayload?.[countKey] ?? 0)

        const confirmed = await requestConfirmation(
          `Delete All ${label.charAt(0).toUpperCase() + label.slice(1)}s`,
          `Delete all ${scopedCount} ${label} records for your user? This cannot be undone.`
        )
        if (!confirmed) {
          return
        }

        const separator = endpoint.includes('?') ? '&' : '?'
        const deleteResponse = await fetch(`${endpoint}${separator}confirm=true`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken ?? ''
          }
        })

        if (!deleteResponse.ok) {
          throw new Error('Failed to delete workers')
        }

        const deletePayload = await deleteResponse.json()
        const deletedCount = Number(deletePayload?.deleted_count ?? 0)
        await onRefresh()
        toast.add({
          severity: 'success',
          summary: 'Bulk Delete Complete',
          detail: `Deleted ${deletedCount} ${label} record(s).`,
          life: 3500
        })
      } catch (e) {
        console.error(e)
        toast.add({
          severity: 'error',
          summary: 'Bulk Delete Failed',
          detail: `Failed to delete ${label} records for your user.`,
          life: 4000
        })
      }
    }

    const handleDeleteAllWorkersForUser = async () => {
      await handleScopedBulkDelete(
        '/api/workers/delete_all/',
        'worker_count',
        'worker',
        async () => {
          await fetchWorkers()
          await hydrateWorkerUserNames()
        }
      )
    }

    const handleDeleteStaleWorkers = async () => {
      const csrfToken = await authStore.getCsrfToken()
      const params = new URLSearchParams({ minutes: '30' })
      if (selectedViewUserFilter.value !== 'all') {
        params.set('username', selectedViewUserFilter.value)
      }
      const endpoint = `/api/workers/delete_stale/?${params.toString()}`

      try {
        const previewResponse = await fetch(endpoint, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken ?? ''
          }
        })

        if (!previewResponse.ok) {
          throw new Error('Failed to preview stale worker cleanup')
        }

        const previewPayload = await previewResponse.json()
        const workerCount = Number(previewPayload?.worker_count ?? 0)
        const confirmed = await requestConfirmation(
          'Delete Stale Workers',
          `Delete ${workerCount} worker(s) with no heartbeat in the last 30 minutes${selectedViewUserFilter.value === 'all' ? '' : ` for ${selectedViewUserFilter.value}`}? This cannot be undone.`
        )
        if (!confirmed) {
          return
        }

        const deleteResponse = await fetch(`${endpoint}&confirm=true`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken ?? ''
          }
        })

        if (!deleteResponse.ok) {
          throw new Error('Failed to delete stale workers')
        }

        const deletePayload = await deleteResponse.json()
        const deletedCount = Number(deletePayload?.deleted_count ?? 0)
        await fetchWorkers()
        await fetchAssignments()
        await fetchJobs()
        await hydrateWorkerUserNames()
        toast.add({
          severity: 'success',
          summary: 'Stale Workers Deleted',
          detail: `Deleted ${deletedCount} stale worker(s).`,
          life: 3500
        })
      } catch (e) {
        console.error(e)
        toast.add({
          severity: 'error',
          summary: 'Delete Failed',
          detail: 'Failed to delete stale workers.',
          life: 4000
        })
      }
    }

    const handleDeleteAllJobsForUser = async () => {
      await handleScopedBulkDelete(
        '/api/jobs/delete_all/',
        'job_count',
        'job',
        async () => {
          await fetchJobs()
          await fetchAssignments()
          await fetchWorkers()
          await hydrateWorkerUserNames()
        }
      )
    }

    const handleDeleteAllAssignmentsForUser = async () => {
      await handleScopedBulkDelete(
        '/api/assignments/delete_all/',
        'assignment_count',
        'assignment',
        async () => {
          await fetchAssignments()
          await fetchWorkers()
          await hydrateWorkerUserNames()
        }
      )
    }

    const handleDeleteAssignment = async (assignmentId?: number) => {
      if (assignmentId == null) {
        console.warn('Cannot delete assignment: missing id')
        return
      }

      const confirmed = await requestConfirmation(
        'Delete Assignment',
        `Delete assignment ${assignmentId}? This cannot be undone.`
      )
      if (!confirmed) {
        return
      }

      try {
        await deleteById('assignments', assignmentId)
        await fetchAssignments()
        toast.add({
          severity: 'success',
          summary: 'Assignment Deleted',
          detail: `Deleted assignment ${assignmentId}`,
          life: 3000
        })
      } catch (e) {
        console.error(e)
        toast.add({
          severity: 'error',
          summary: 'Delete Failed',
          detail: `Could not delete assignment ${assignmentId}`,
          life: 3500
        })
      }
    }

    const goToAdminNotifications = () => {
      router.push('/admin-notifications')
    }

    const goToSchemesOfAccess = () => {
      router.push('/schemes-of-access')
    }

    const goToCommandControlPanel = () => {
      router.push('/admin-workflows')
    }

    return {
      workers,
      workerKeys,
      leaders,
      jobs,
      jobKeys,
      assignments,
      assignmentKeys,
      eventLogs,
      eventLogKeys,
      expandedDetails,
      formatHeaderKey,
      formatArgs,
      formatDetails,
      toggleDetails,
      tableConfig,
      helpDrawer,
      activeTab,
      highlightedRowId,
      showScrollToTop,
      scrollToTop,
      completionImporting,
      completionImportSummary,
      completionReport,
      handleImportCompletionHistory,
      projectsInterface,
      handleCleanupSystem,
      handleClearEventLog,
      handleRefreshAll,
      handleManualRefreshAll,
      handleTabChange,
      handleCreateTestWorker,
      handleUpdateTestWorker,
      handleDeleteTestWorker,
      hasTestWorker,
      workerCount,
      jobCount,
      assignmentCount,
      assignedWorkerCount,
      workerUtilizationPercent,
      workerUtilizationColor,
      error,
      success,
      isDetailsTruncated,
      goToAdminNotifications,
      goToSchemesOfAccess,
      goToCommandControlPanel,
      workerControlOptions,
      totalCount,
      currentPage,
      pageSize,
      loading,
      hasNext,
      hasPrevious,
      totalPages,
      nextPage,
      previousPage,
      goToPage,
      changePageSize,
      handlePageSizeChange,
      getWorkerTooltip,
      workerActionOptions,
      getWorkerActionSelection,
      setWorkerActionSelection,
      getWorkerActionLabel,
      canRunWorkerAction,
      runWorkerAction,
      getWorkerHeartbeatClass,
      getWorkerHeartbeatClassFromWorker,
      getLeaderHeartbeatClass,
      formatHeartbeatAge,
      getWorkerDisplay,
      getProjectDisplay,
      formatDateTime,
      getWorkerCompletedJobCount,
      getWorkerAverageTimingSeconds,
      formatTiming,
      formatCapabilities,
      formatCapabilityLabel,
      getWorkerCapabilities,
      getWorkerUserDisplay,
      getCompletedJobsTooltip,
      recentCompletionsCount,
      recentCompletionsLastAtLabel,
      recentCompletionsTooltip,
      selectedViewUserFilter,
      userFilterOptions,
      filteredSortedJobs,
      setJobSortFromHeader,
      getJobSortIndicatorForHeader,
      hasWorkerAssignment,
      getAssignmentByWorkerId,
      getWorkerControlProjectId,
      getWorkerControlSelection,
      setWorkerControlSelection,
      isWorkerControlSubmitting,
      submitWorkerControlJob,
      goToWorkerAssignment,
      canLinkToWorker,
      canLinkToJob,
      goToWorkerFromJob,
      goToJobFromAssignment,
      getJobDisplay,
      sortedWorkers,
      setWorkerSort,
      getWorkerSortIndicator,
      sortedAssignments,
      setAssignmentSort,
      getAssignmentSortIndicator,
      setAssignmentSortFromHeader,
      getAssignmentSortIndicatorForHeader,
      getWorkerRowId,
      getJobRowId,
      getAssignmentRowId,
      workersColumnCount,
      handleDeleteJob,
      handleDeleteWorker,
      handleDeleteLeader,
      handleDeleteAllWorkersForUser,
      handleDeleteStaleWorkers,
      handleDeleteAllJobsForUser,
      handleDeleteAllAssignmentsForUser,
      handleDeleteAssignment
    }
  }
})

</script>


<template>
  <h1>Processing</h1>
  <div class="header-actions">
    <Button @click="helpDrawer.visible=true">?</Button>
    <Button @click="handleManualRefreshAll" class="refresh-button">Refresh All</Button>
  </div>
  <div class="stats-strip">
    <div class="stat-card">
      <div class="stat-label">Workers</div>
      <div class="stat-value">{{ workerCount }}</div>
    </div>
    <div class="stat-card">
      <div class="stat-label">Jobs</div>
      <div class="stat-value">{{ jobCount }}</div>
    </div>
    <div class="stat-card">
      <div class="stat-label">Assignments</div>
      <div class="stat-value">{{ assignmentCount }}</div>
    </div>
    <div class="stat-card stat-wide">
      <div class="utilization-header">
        <span class="stat-label">Worker Utilization</span>
        <span class="utilization-percent" :style="{ color: workerUtilizationColor }">{{ workerUtilizationPercent }}%</span>
      </div>
      <div class="utilization-bar">
        <div
          class="utilization-fill"
          :style="{ width: `${workerUtilizationPercent}%`, backgroundColor: workerUtilizationColor }"
        />
      </div>
      <div class="utilization-meta">
        {{ assignedWorkerCount }} / {{ workerCount }} workers assigned
      </div>
    </div>
    <div class="stat-card tooltip" :data-fulltext="recentCompletionsTooltip">
      <div class="stat-label">Recent Job Completions</div>
      <div class="stat-value">{{ recentCompletionsCount }}</div>
      <div class="utilization-meta">{{ recentCompletionsLastAtLabel }}</div>
    </div>
  </div>
  <div class="view-filter-bar">
    <label for="view-user-filter">User filter</label>
    <select id="view-user-filter" v-model="selectedViewUserFilter" class="table-filter-select">
      <option value="all">All users</option>
      <option v-for="username in userFilterOptions" :key="username" :value="username">{{ username }}</option>
    </select>
  </div>
  <Tabs :value="activeTab" @update:value="handleTabChange">
    <TabList>
      <Tab value="projects">Active Projects</Tab>
      <Tab value="jobs">Jobs queued</Tab>
      <Tab value="workers">Workers</Tab>
      <Tab value="leaders">Leaders</Tab>
      <Tab value="assignments">Assigned Jobs</Tab>
      <Tab value="events">Event Log</Tab>
      <Tab value="admin">Admin</Tab>
      <Tab value="pipeline">Pipeline Overview</Tab>
      <Tab value="command-control">Command Control</Tab>
    </TabList>  
    <TabPanels>
      <TabPanel value="projects">
        <ActiveProcessing />
      </TabPanel>
      <TabPanel value="jobs">
        <h2>Jobs queued</h2>
        <div class="tab-toolbar">
          <Button class="action-delete-bulk" severity="danger" text size="small" icon="pi pi-trash" @click="handleDeleteAllJobsForUser">
            Delete All Jobs
          </Button>
          <div class="jobs-controls">
            <span class="sort-note">Default sort: last modified</span>
          </div>
        </div>
        <table>
          <colgroup>
            <col v-for="(width, index) in tableConfig.jobs.columnWidths" :key="index" :style="{ width }" />
          </colgroup>
          <thead>
            <tr>
              <th v-for="(header, index) in tableConfig.jobs.headers" :key="index">
                <button type="button" class="sort-button" @click="setJobSortFromHeader(header)">
                  {{ formatHeaderKey(header) }} <span class="sort-indicator">{{ getJobSortIndicatorForHeader(header) }}</span>
                </button>
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="job in filteredSortedJobs" :key="job.id" :id="getJobRowId(job.id)" :class="{ 'row-target-highlight': highlightedRowId === getJobRowId(job.id) }">
              <td
                v-for="(header, index) in tableConfig.jobs.headers"
                :key="index"
                :data-fulltext="header === 'worker' ? getWorkerTooltip(job[header]) : header === 'project' ? getProjectDisplay(job[header]) : job[header]"
                class="tooltip"
              >
                <template v-if="header === 'args'">
                  {{ formatArgs(job[header]) }}
                </template>
                <template v-else-if="header === 'worker'">
                  <button
                    v-if="canLinkToWorker(job[header])"
                    type="button"
                    class="table-link"
                    @click="goToWorkerFromJob(job[header])"
                  >
                  <span :class="['worker-value', getWorkerHeartbeatClass(job[header])]">
                    {{ getWorkerDisplay(job[header]) }}
                  </span>
                  </button>
                  <span v-else :class="['worker-value', getWorkerHeartbeatClass(job[header])]">
                    {{ getWorkerDisplay(job[header]) }}
                  </span>
                </template>
                <template v-else-if="header === 'project'">
                  {{ getProjectDisplay(job[header]) }}
                </template>
                <template v-else-if="header === 'created' || header === 'updated'">
                  {{ formatDateTime(job[header]) }}
                </template>
                <template v-else-if="header === 'id'">
                  <a
                    class="raw-id-link"
                    :href="`/api/jobs/${job.id}/`"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ job[header] }}
                  </a>
                </template>
                <template v-else>
                  {{ job[header] }}
                </template>
              </td>
              <td>
                <Button class="action-delete-row" severity="danger" text size="small" icon="pi pi-trash" @click="handleDeleteJob(job.id)">
                  Delete
                </Button>
              </td>
            </tr>
            <tr v-if="filteredSortedJobs.length === 0">
              <td :colspan="tableConfig.jobs.headers.length + 1">No jobs queued.</td>
            </tr>
          </tbody>
        </table>
      </TabPanel>
      <TabPanel value="workers">
        <h2>Workers</h2>
        <div class="tab-toolbar">
          <Button
            class="action-delete-bulk"
            severity="info"
            text
            size="small"
            icon="pi pi-chart-bar"
            :loading="completionImporting"
            :disabled="completionImporting"
            @click="handleImportCompletionHistory"
          >
            Import Completion History & Build Report
          </Button>
          <Button class="action-delete-bulk" severity="danger" text size="small" icon="pi pi-trash" @click="handleDeleteAllWorkersForUser">
            Delete All Workers
          </Button>
          <Button class="action-delete-bulk" severity="warn" text size="small" icon="pi pi-clock" @click="handleDeleteStaleWorkers">
            Delete Stale Workers
          </Button>
        </div>
        <table>
          <colgroup>
            <col v-for="(width, index) in tableConfig.workers.columnWidths" :key="index" :style="{ width }" />
          </colgroup>
          <thead>
            <tr>
              <th>
                <button type="button" class="sort-button" @click="setWorkerSort('id')">
                  ID <span class="sort-indicator">{{ getWorkerSortIndicator('id') }}</span>
                </button>
              </th>
              <th>
                <button type="button" class="sort-button" @click="setWorkerSort('machine_id')">
                  Worker <span class="sort-indicator">{{ getWorkerSortIndicator('machine_id') }}</span>
                </button>
              </th>
              <th>Software Version</th>
              <th>
                <button type="button" class="sort-button" @click="setWorkerSort('last_seen')">
                  Heartbeat <span class="sort-indicator">{{ getWorkerSortIndicator('last_seen') }}</span>
                </button>
              </th>
              <th>
                <button type="button" class="sort-button" @click="setWorkerSort('completed_jobs')">
                  Completed Jobs <span class="sort-indicator">{{ getWorkerSortIndicator('completed_jobs') }}</span>
                </button>
              </th>
              <th>
                <button type="button" class="sort-button" @click="setWorkerSort('avg_timing')">
                  Avg Timing <span class="sort-indicator">{{ getWorkerSortIndicator('avg_timing') }}</span>
                </button>
              </th>
              <th>
                <button type="button" class="sort-button" @click="setWorkerSort('capabilities')">
                  Capabilities <span class="sort-indicator">{{ getWorkerSortIndicator('capabilities') }}</span>
                </button>
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in sortedWorkers" :key="item.id" :id="getWorkerRowId(item.id)" :class="{ 'row-target-highlight': highlightedRowId === getWorkerRowId(item.id) }">
              <td :data-fulltext="String(item.id ?? '')" class="tooltip">
                <a
                  class="raw-id-link"
                  :href="`/api/workers/${item.id}/`"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {{ item.id }}
                </a>
              </td>
              <td :data-fulltext="`${item.machine_id}\nUser: ${getWorkerUserDisplay(item)}`" class="tooltip">
                <div class="worker-primary">{{ item.machine_id }}</div>
                <div class="worker-secondary">User: {{ getWorkerUserDisplay(item) }}</div>
              </td>
              <td :data-fulltext="item.processing_software_version" class="tooltip">
                <span class="worker-version">{{ item.processing_software_version }}</span>
              </td>
              <td :data-fulltext="`Last seen: ${formatDateTime(item.last_seen || item.updated)}\nAge: ${formatHeartbeatAge(item.last_seen || item.updated)}`" class="tooltip">
                <div :class="['worker-primary', getWorkerHeartbeatClassFromWorker(item)]">
                  {{ formatHeartbeatAge(item.last_seen || item.updated) }}
                </div>
                <div class="worker-secondary">{{ formatDateTime(item.last_seen || item.updated) }}</div>
                <button
                  v-if="hasWorkerAssignment(item)"
                  type="button"
                  class="assignment-link"
                  @click="goToWorkerAssignment(item)"
                >
                  ● Assigned #{{ getAssignmentByWorkerId(item.id)?.id }}
                </button>
              </td>
              <td :data-fulltext="getCompletedJobsTooltip(item)" class="tooltip">
                {{ getWorkerCompletedJobCount(item) }}
              </td>
              <td :data-fulltext="`Average timing: ${formatTiming(getWorkerAverageTimingSeconds(item))}`" class="tooltip">
                {{ formatTiming(getWorkerAverageTimingSeconds(item)) }}
              </td>
              <td :data-fulltext="formatCapabilities(item)" class="tooltip">
                <div class="capability-list">
                  <span
                    v-for="capability in getWorkerCapabilities(item)"
                    :key="`${item.id}-${capability}`"
                    class="capability-chip"
                  >
                    {{ formatCapabilityLabel(capability) }}
                  </span>
                  <span v-if="getWorkerCapabilities(item).length === 0" class="worker-secondary">none</span>
                </div>
              </td>
              <td class="actions-cell">
                <div class="worker-control-group">
                  <Select
                    class="worker-control-select"
                    :modelValue="getWorkerActionSelection(item)"
                    :options="workerActionOptions"
                    optionLabel="label"
                    optionValue="value"
                    appendTo="body"
                    @update:modelValue="setWorkerActionSelection(item, String($event ?? 'delete'))"
                  />
                  <Button
                    class="worker-control-button"
                    size="small"
                    :severity="getWorkerActionSelection(item) === 'delete' ? 'danger' : 'secondary'"
                    :disabled="!canRunWorkerAction(item)"
                    @click="runWorkerAction(item)"
                  >
                    {{ getWorkerActionLabel(item) }}
                  </Button>
                </div>
              </td>
            </tr>
            <tr v-if="sortedWorkers.length === 0">
              <td :colspan="workersColumnCount">No workers connected.</td>
            </tr>
          </tbody>
        </table>
        <section v-if="completionImportSummary || completionReport.length" class="completion-report">
          <h3>Completion Timing Summary</h3>
          <p v-if="completionImportSummary" class="utilization-meta">{{ completionImportSummary }}</p>
          <table>
            <thead>
              <tr>
                <th>Project</th>
                <th>Batch</th>
                <th>Job</th>
                <th>Status</th>
                <th>Samples</th>
                <th>Average Runtime</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in completionReport" :key="`${row.project_id_snapshot}-${row.batch}-${row.job_name}-${row.status}-${index}`">
                <td>{{ row.project_name || 'Unknown project' }} <span class="worker-secondary">#{{ row.project_id_snapshot ?? '?' }}</span></td>
                <td>{{ row.batch || 'Not specified' }}</td>
                <td>{{ row.job_name }}</td>
                <td>{{ row.status || 'Unknown' }}</td>
                <td>{{ row.timed_sample_count }} / {{ row.completion_count }}</td>
                <td>{{ row.average_runtime_seconds == null ? 'n/a' : `${Number(row.average_runtime_seconds).toFixed(1)}s` }}</td>
              </tr>
              <tr v-if="completionReport.length === 0">
                <td colspan="6">No valid completions have been imported.</td>
              </tr>
            </tbody>
          </table>
        </section>
      </TabPanel>
      <TabPanel value="leaders">
        <h2>Leaders</h2>
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>User</th>
              <th>Leader</th>
              <th>Status</th>
              <th>Software Version</th>
              <th>Heartbeat</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="leader in leaders" :key="leader.id">
              <td>
                <a class="raw-id-link" :href="`/api/leaders/${leader.id}/`" target="_blank" rel="noopener noreferrer">
                  {{ leader.id }}
                </a>
              </td>
              <td>{{ leader.user }}</td>
              <td :data-fulltext="leader.machine_id" class="tooltip">
                <div class="worker-primary">{{ leader.machine_id }}</div>
              </td>
              <td :data-fulltext="leader.status || 'Not reported'" class="tooltip">
                {{ leader.status || 'Not reported' }}
              </td>
              <td :data-fulltext="leader.processing_software_version" class="tooltip">
                <span class="worker-version">{{ leader.processing_software_version }}</span>
              </td>
              <td :data-fulltext="`Last heartbeat: ${formatHeartbeatAge(leader.last_seen || leader.updated)}`" class="tooltip">
                <div :class="['worker-primary', getLeaderHeartbeatClass(leader)]">
                  {{ formatHeartbeatAge(leader.last_seen || leader.updated) }}
                </div>
                <div class="worker-secondary">{{ formatDateTime(leader.last_seen || leader.updated) }}</div>
              </td>
              <td class="actions-cell">
                <Button
                  class="action-delete-row"
                  severity="danger"
                  text
                  size="small"
                  icon="pi pi-trash"
                  @click="handleDeleteLeader(leader.id)"
                >
                  Delete
                </Button>
              </td>
            </tr>
            <tr v-if="leaders.length === 0">
              <td colspan="7">No leaders registered.</td>
            </tr>
          </tbody>
        </table>
      </TabPanel>
      <TabPanel value="assignments">
        <h2>Assigned Jobs</h2>
        <Button class="action-delete-bulk" severity="danger" text size="small" icon="pi pi-trash" @click="handleDeleteAllAssignmentsForUser">
          Delete All Assignments
        </Button>
        <table>
          <colgroup>
            <col v-for="(width, index) in tableConfig.assignments.columnWidths" :key="index" :style="{ width }" />
          </colgroup>
          <thead>
            <tr>
              <th v-for="(header, index) in tableConfig.assignments.headers" :key="index">
                <button type="button" class="sort-button" @click="setAssignmentSortFromHeader(header)">
                  {{ formatHeaderKey(header) }} <span class="sort-indicator">{{ getAssignmentSortIndicatorForHeader(header) }}</span>
                </button>
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="assignment in sortedAssignments" :key="assignment.id" :id="getAssignmentRowId(assignment.id)" :class="{ 'row-target-highlight': highlightedRowId === getAssignmentRowId(assignment.id) }">
              <td
                v-for="(header, index) in tableConfig.assignments.headers"
                :key="index"
                :data-fulltext="header === 'worker' ? getWorkerTooltip(assignment[header]) : assignment[header]"
                class="tooltip"
              >
                <template v-if="header === 'job'">
                  <button
                    v-if="canLinkToJob(assignment[header])"
                    type="button"
                    class="table-link"
                    @click="goToJobFromAssignment(assignment[header])"
                  >
                    {{ getJobDisplay(assignment[header]) }}
                  </button>
                  <span v-else>
                    {{ getJobDisplay(assignment[header]) }}
                  </span>
                </template>
                <template v-else-if="header === 'worker'">
                  <span :class="['worker-value', getWorkerHeartbeatClass(assignment[header])]">
                    {{ getWorkerDisplay(assignment[header]) }}
                  </span>
                </template>
                <template v-else-if="header === 'id'">
                  <a
                    class="raw-id-link"
                    :href="`/api/assignments/${assignment.id}/`"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ assignment[header] }}
                  </a>
                </template>
                <template v-else>
                  {{ assignment[header] }}
                </template>
              </td>
              <td>
                <Button class="action-delete-row" severity="danger" text size="small" icon="pi pi-trash" @click="handleDeleteAssignment(assignment.id)">
                  Delete
                </Button>
              </td>
            </tr>
            <tr v-if="sortedAssignments.length === 0">
              <td :colspan="tableConfig.assignments.headers.length + 1">No assigned jobs.</td>
            </tr>
          </tbody>
        </table>
      </TabPanel>
      <TabPanel value="events">
        <h2>Event Log</h2>
        
        <!-- Pagination Controls - Top -->
        <div class="pagination-controls">
          <div class="pagination-info">
            <span v-if="loading">Loading...</span>
            <span v-else>
              Showing {{ ((currentPage - 1) * pageSize) + 1 }} to {{ Math.min(currentPage * pageSize, totalCount) }} of {{ totalCount }} events
            </span>
          </div>
          <div class="pagination-actions">
            <label for="pageSize">Page Size:</label>
            <select id="pageSize" :value="pageSize" @change="handlePageSizeChange">
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
            </select>
            
            <Button @click="previousPage" :disabled="!hasPrevious || loading">Previous</Button>
            <span class="page-info">Page {{ currentPage }} of {{ totalPages }}</span>
            <Button @click="nextPage" :disabled="!hasNext || loading">Next</Button>
          </div>
        </div>

        <table>
          <colgroup>
            <col v-for="(width, index) in tableConfig.eventLog.columnWidths" :key="index" :style="{ width }" />
          </colgroup>
          <thead>
            <tr>
              <th v-for="(header, index) in tableConfig.eventLog.headers" :key="index">{{ formatHeaderKey(header) }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="log in eventLogs" :key="log.id">
              <td v-for="(header, index) in tableConfig.eventLog.headers" :key="index" :data-fulltext="log[header]" class="tooltip">
                <template v-if="header === 'details'">
                  <div 
                    class="details-container" 
                    @click="toggleDetails(log.id)"
                    :class="{ 'truncated': isDetailsTruncated(log[header]) && !expandedDetails[log.id] }"
                  >
                    <pre class="details-json">{{ formatDetails(log[header], log.id) }}</pre>
                    <div v-if="isDetailsTruncated(log[header])" class="expand-indicator">
                      {{ expandedDetails[log.id] ? '▲ Collapse' : '▼ Expand' }}
                    </div>
                  </div>
                </template>
                <template v-else>
                  {{ log[header] }}
                </template>
              </td>
            </tr>
            <tr v-if="eventLogs.length === 0 && !loading">
              <td :colspan="tableConfig.eventLog.headers.length">No events logged.</td>
            </tr>
            <tr v-if="loading">
              <td :colspan="tableConfig.eventLog.headers.length">Loading...</td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination Controls - Bottom -->
        <div class="pagination-controls">
          <div class="pagination-actions">
            <Button @click="previousPage" :disabled="!hasPrevious || loading">Previous</Button>
            <span class="page-info">Page {{ currentPage }} of {{ totalPages }}</span>
            <Button @click="nextPage" :disabled="!hasNext || loading">Next</Button>
          </div>
        </div>
      </TabPanel>
      <TabPanel value="admin">
        <h2>Admin Panel</h2>
        <div class="admin-panel">
          <div class="admin-message" v-if="error" style="color: red">
            {{ error }}
          </div>
          <div class="admin-message" v-if="success" style="color: green">
            {{ success }}
          </div>
          <div class="admin-actions">
            <Button @click="handleCleanupSystem" class="danger-button">
              Delete All Jobs, Workers, and Assignments
            </Button>
            <Button @click="handleClearEventLog" class="danger-button">
              Clear Event Log
            </Button>
            <div class="test-worker-actions">
              <h3>Test Worker Actions</h3>
              <Button @click="handleCreateTestWorker" class="test-button">
                Create Test Worker
              </Button>
              <Button 
                @click="handleUpdateTestWorker" 
                class="test-button"
                :disabled="!hasTestWorker"
              >
                Update Test Worker
              </Button>
              <Button 
                @click="handleDeleteTestWorker" 
                class="test-button"
                :disabled="!hasTestWorker"
              >
                Delete Test Worker
              </Button>
              <div class="admin-notifications">
                <h3>Admin Notifications</h3>
                <a @click="goToAdminNotifications">Notifications</a>
              </div>
              <div class="schemes-of-access">
                <h3>Schemes of Access</h3>
                <a @click="goToSchemesOfAccess">Schemes of Access</a>
              </div>
              <div class="command-control-panel">
                <h3>Command Control Panel</h3>
                <a @click="goToCommandControlPanel">Workflow Management</a>
              </div>
            </div>

          </div>
        </div>
      </TabPanel>
      <TabPanel value="pipeline">
        <WorkflowPipelineView />
      </TabPanel>
      <TabPanel value="command-control">
        <div class="command-control-launch">
          <router-link :to="{ name: 'Command Control Panel' }" class="command-control-link">
            Open full-screen Command Control Panel
          </router-link>
        </div>
        <CommandControlPanelView />
      </TabPanel>
    </TabPanels>
  </Tabs>
  <Button
    v-if="showScrollToTop"
    class="scroll-to-top-button"
    icon="pi pi-arrow-up"
    aria-label="Scroll to top"
    v-tooltip.top="'Scroll to top'"
    @click="scrollToTop"
  />

</template>

<style scoped>
.wrapper {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
}

.header-actions {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.tab-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.view-filter-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #374151;
}

.worker-control-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.worker-control-select {
  min-width: 210px;
}

.worker-control-button {
  width: 100%;
}

.jobs-controls {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #374151;
}

.table-filter-select {
  height: 30px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0 8px;
  background: #fff;
  color: #111827;
}

.sort-note {
  color: #6b7280;
}

.stats-strip {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.stat-card {
  border: 1px solid #d9e3f5;
  border-radius: 8px;
  padding: 12px;
  background: #f8fbff;
}

.stat-wide {
  grid-column: span 2;
}

.stat-label {
  font-size: 12px;
  color: #4b5563;
}

.stat-value {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.2;
}

.utilization-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.utilization-percent {
  font-size: 22px;
  font-weight: 700;
}

.utilization-bar {
  height: 10px;
  width: 100%;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
}

.utilization-fill {
  height: 100%;
  transition: width 0.2s ease, background-color 0.2s ease;
}

.utilization-meta {
  margin-top: 8px;
  font-size: 12px;
  color: #4b5563;
}

@media (max-width: 900px) {
  .stat-wide {
    grid-column: span 1;
  }
}

.refresh-button {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.worker-control-note {
  margin: 0 0 12px;
  color: #4b5563;
  font-size: 14px;
}

.refresh-button:hover {
  background-color: #0056b3;
}

.table-container {
  flex: 1 1 30%;
  margin: 10px;
  min-width: 100px;
}

table {
  width: 100%;
  border-collapse: collapse;
  word-wrap: break-word;
  table-layout: fixed;
}

col.col-10 {
  width: 10%;
}

col.col-40 {
  width: 40%;
}

th, td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  position: relative;
}

th {
  white-space: normal;
  text-wrap: balance;
}

td {
  white-space: nowrap;
}

.actions-cell {
  white-space: normal;
}

.worker-control-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
  min-width: 220px;
}

.worker-control-select {
  width: 100%;
  min-width: 220px;
}

.worker-control-button {
  width: 100%;
}

th:hover, td:hover {
  background-color: #f9f9f9; /* Optional: highlight cell on hover */
  overflow: visible; /* Prevent clipping */
  text-wrap: auto; /* Allow text wrapping */
}

.tooltip::after {
  content: attr(data-fulltext); /* Use the full text for the tooltip */
  position: absolute;
  background-color: #333; /* Tooltip background */
  color: #fff; /* Tooltip text color */
  padding: 4px 8px;
  border-radius: 4px;
  white-space: pre-line; /* Support line breaks in hydrated worker tooltip */
  font-size: 12px;
  display: none; /* Hidden by default */
  z-index: 10;
  top: 100%; /* Position below the cell */
  left: 50%;
  transform: translateX(-50%);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  overflow: visible;
}

.tooltip:hover::after {
  display: block; /* Show tooltip on hover */
  overflow: visible; /* Prevent clipping */
}

.details-container {
  cursor: pointer;
  position: relative;
  padding-bottom: 16px; /* Space for the indicator */
}

.details-container:hover {
  background-color: #f0f0f0;
}

.details-json {
  white-space: pre-wrap;
  font-family: monospace;
  font-size: 12px;
  max-height: 200px;
  overflow-y: auto;
  background-color: #f5f5f5;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #ddd;
  margin: 0;
}

.expand-indicator {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  font-size: 11px;
  color: #666;
  background-color: rgba(240, 240, 240, 0.8);
  padding: 2px 8px;
  border-radius: 4px;
  border-top: 1px solid #ddd;
}

.truncated .details-json {
  position: relative;
}

.admin-panel {
  padding: 20px;
}

.command-control-launch {
  margin-bottom: 1rem;
}

.command-control-link {
  color: var(--primary-color, #3b82f6);
  font-weight: 600;
  text-decoration: none;
}

.command-control-link:hover {
  text-decoration: underline;
}

.admin-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
}

.danger-button {
  background-color: #dc3545;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
}

.danger-button:hover {
  background-color: #c82333;
}

.admin-message {
  margin-bottom: 20px;
  padding: 10px;
  border-radius: 4px;
}

.test-worker-actions {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #ddd;
}

.test-worker-actions h3 {
  margin-bottom: 10px;
}

.test-button {
  background-color: #28a745;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 10px;
}

.test-button:hover {
  background-color: #218838;
}

.test-button:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.toggle-details-button {
  display: none; /* Hide the button since we're not using it anymore */
}

.pagination-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
}

.pagination-info {
  font-size: 12px;
  color: #666;
}

.pagination-actions {
  display: flex;
  gap: 10px;
}

.page-info {
  font-size: 12px;
  color: #666;
}

.sort-button {
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
}

.sort-indicator {
  font-size: 11px;
  color: #6b7280;
}

.worker-primary {
  font-weight: 600;
}

.row-target-highlight {
  outline: 3px solid #dc2626;
  outline-offset: -3px;
  animation: row-target-pulse 2.6s ease-out forwards;
}

.row-target-highlight > td {
  background-color: #fff1f2;
}

@keyframes row-target-pulse {
  0%, 35% {
    outline-color: #dc2626;
    outline-width: 4px;
  }
  100% {
    outline-color: transparent;
    outline-width: 2px;
  }
}

.scroll-to-top-button {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 1000;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.22);
}

@media (max-width: 600px) {
  .scroll-to-top-button {
    right: 14px;
    bottom: 14px;
  }
}

.worker-secondary {
  color: #6b7280;
  font-size: 12px;
}

.worker-user {
  font-weight: 600;
  color: #374151;
}

.worker-version,
.worker-capabilities {
  white-space: normal;
  word-break: break-word;
}

.capability-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.capability-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: #e0e7ff;
  color: #1e3a8a;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
}

.assignment-link {
  margin-top: 4px;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  color: #0f766e;
  font-size: 12px;
  text-decoration: underline;
}

.assignment-link:hover {
  color: #115e59;
}

.table-link {
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  color: #1d4ed8;
  text-decoration: underline;
  cursor: pointer;
  font: inherit;
  text-align: left;
}

.table-link:hover {
  color: #1e40af;
}

.actions-cell {
  min-width: 92px;
}

.action-delete-bulk {
  margin-bottom: 10px;
  border-radius: 999px;
  padding: 6px 14px;
  border: 1px solid #f3b3b3;
  background: #fff3f3;
  color: #991b1b;
  font-weight: 600;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.action-delete-bulk:hover {
  background: #fee2e2;
  border-color: #ef4444;
  color: #7f1d1d;
}

.worker-value {
  font-weight: 600;
}

.raw-id-link {
  color: #2563eb;
  text-decoration: underline;
}

.raw-id-link:hover {
  color: #1d4ed8;
}

.heartbeat-fresh {
  color: #16a34a;
}

.heartbeat-warning {
  color: #ea580c;
}

.heartbeat-stale {
  color: #dc2626;
}

:deep(.action-delete) {
  white-space: nowrap;
  min-width: 84px;
  border-radius: 999px;
  border: 1px solid #f5c2c7;
  background: #fff6f7;
  color: #9f1239;
  justify-content: center;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

:deep(.action-delete-row:hover) {
  background: #ffe4e6;
  border-color: #fb7185;
  color: #881337;
}

:deep(.action-delete-bulk) {
  justify-content: center;
}
</style>
